import { ReleaseEntry } from "./homeworkReleasePlan";

type DocumentData = Record<string, unknown>;
interface ReleaseContentReader {
    readReport: (path: string) => Promise<DocumentData | undefined>;
    readContent: (path: string) => Promise<Record<string, DocumentData>>;
}

const fields = {
    word: { modes: ["focus", "write", "painting"], numbers: ["word_repeat", "example_repeat", "word_write", "cutline"] },
    sentence: { modes: ["listening", "order", "record"], numbers: ["cutline", "sentence_repeat", "blank", "pass"] },
    video: { modes: ["video"], numbers: ["main_repeat", "video_repeat"] },
};

/** Validate source data once per report, before any assignment is written. */
export const validateReleaseContent = async (entries: ReleaseEntry[], reader: ReleaseContentReader): Promise<void> => {
    const unique = new Map<string, ReleaseEntry>();
    for (const entry of entries) {
        const report = entry.data.report as { path?: string; id?: string } | undefined;
        if (!report?.path || !report.id) throw new Error("배포할 과제 경로를 확인해주세요.");
        unique.set(`${report.path}/${report.id}:${JSON.stringify(entry.data.report_setting || null)}`, entry);
    }
    const reports = Array.from(unique.values());
    for (let offset = 0; offset < reports.length; offset += 4) {
        await Promise.all(reports.slice(offset, offset + 4).map(async entry => {
            const report = entry.data.report as { path: string; id: string };
            const path = `${report.path}/${report.id}`;
            const header = await reader.readReport(path);
            if (!header || header.type !== "report") throw new Error(`과제(${report.id})가 삭제되었거나 올바르지 않습니다. 다시 선택해주세요.`);
            const label = typeof header.name === "string" ? header.name : report.id;
            const content = await reader.readContent(`${path}/Content`);
            const settings = entry.data.report_setting || content.setting;
            if (!settings || typeof settings !== "object") {
                throw new Error(`${label}: 학습 설정이 없습니다. 학습설정을 저장하거나 배포 학습설정을 지정해주세요.`);
            }
            let hasContent = false;
            let hasEnabledLearning = false;
            for (const type of Object.keys(fields) as (keyof typeof fields)[]) {
                const items = content[type]?.content;
                if (!Array.isArray(items) || !items.length) continue;
                hasContent = true;
                const section = (settings as DocumentData)[type] as DocumentData | undefined;
                const schema = fields[type];
                if (!section || typeof section !== "object" ||
                    schema.modes.some(mode => typeof section[mode] !== "boolean") ||
                    schema.numbers.some(field => typeof section[field] !== "number" || !Number.isFinite(section[field]) || (section[field] as number) < 0)) {
                    throw new Error(`${label}: ${type} 학습 설정이 올바르지 않습니다. 학습설정을 다시 저장해주세요.`);
                }
                hasEnabledLearning ||= schema.modes.some(mode => section[mode] === true);
            }
            if (!hasContent) throw new Error(`${label}: 저장된 학습 콘텐츠가 없습니다. 콘텐츠를 먼저 저장해주세요.`);
            if (!hasEnabledLearning) throw new Error(`${label}: 활성화된 학습 항목이 없습니다. 학습설정을 확인해주세요.`);
        }));
    }
};
